import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BrandLink from "../components/BrandLink";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { DEV_AUTH_ENABLED } from "../lib/devAuth";
import { loginContextForNext, sanitizeLoginNext } from "../lib/loginCopy";
import { backLabel, goBack } from "../lib/navBack";
import { signInWithGoogle, signInWithApple, formatAuthError } from "../lib/supabaseAuth";
import SocialAuthButtons from "../components/SocialAuthButtons";
import FormLabel from "../components/FormLabel";
import { ReadableCard, ReadableNavButton } from "../components/Sticker";

const MAIN_PADDING_BOTTOM = DEV_AUTH_ENABLED
  ? "pb-[calc(4.75rem+env(safe-area-inset-bottom,0px))]"
  : "pb-6";

export default function LoginPage() {
  const loc = useLocation();
  const nav = useNavigate();
  const [loadingProvider, setLoadingProvider] = useState(null);

  const redirectTo = sanitizeLoginNext(new URLSearchParams(loc.search).get("next"));
  const { headline, detail, pageTitle, footnote } = loginContextForNext(redirectTo);
  const loading = loadingProvider !== null;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = pageTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [pageTitle]);

  async function handleSocialSignIn(provider, fn) {
    setLoadingProvider(provider);
    try {
      await fn(redirectTo);
    } catch (err) {
      const msg = err?.response ? formatApiError(err) : formatAuthError(err);
      if (msg !== "Sign-in cancelled.") toast.error(msg);
    } finally {
      setLoadingProvider(null);
    }
  }

  const loadingMessage =
    loadingProvider === "google"
      ? "Opening Google sign-in…"
      : loadingProvider === "apple"
        ? "Opening Apple sign-in…"
        : null;

  return (
    <div className="min-h-[100dvh] bg-canvas text-ink font-body flex flex-col">
      <header className="max-w-page mx-auto w-full px-4 pt-4 pb-2 flex items-center justify-between gap-3 shrink-0">
        <ReadableNavButton
          type="button"
          onClick={() => goBack(nav, loc, ["/"])}
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
          {backLabel(loc, "Home")}
        </ReadableNavButton>
        <BrandLink
          to="/"
          variant="readable"
          size="sm"
          testId="login-brand-link"
          aria-label="rsvp.dance home"
        />
      </header>

      <main
        className={`flex-1 flex flex-col justify-center w-full max-w-page mx-auto px-4 py-6 ${MAIN_PADDING_BOTTOM} sm:py-10 sm:pb-12`}
      >
        <ReadableCard
          className="w-full max-w-md mx-auto flex flex-col"
          data-testid="login-auth-card"
        >
          <div className="px-5 pt-6 sm:px-6 sm:pt-6 space-y-3">
            <h1 className="font-ui font-semibold text-[22px] sm:text-[26px] leading-tight text-balance">{headline}</h1>
            <p className="font-ui font-medium text-[14px] sm:text-[15px] text-ink/80 max-w-[65ch] text-pretty leading-relaxed">
              {detail}
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-black/[0.06] px-5 pb-6 sm:px-6 sm:pb-6 flex flex-col gap-4">
            <fieldset className="flex flex-col gap-4 border-0 p-0 m-0 min-w-0" disabled={loading}>
              <legend className="sr-only">Sign-in options</legend>
              <div aria-live="polite" aria-busy={loading}>
                {loadingMessage ? (
                  <p className="sr-only" role="status">
                    {loadingMessage}
                  </p>
                ) : null}
                <FormLabel as="p" id="sign-in-options-label" className="mb-3 block">
                  Choose Google or Apple
                </FormLabel>
                <SocialAuthButtons
                  readable
                  loading={loading}
                  loadingProvider={loadingProvider}
                  stacked
                  aria-labelledby="sign-in-options-label"
                  onGoogle={() => handleSocialSignIn("google", signInWithGoogle)}
                  onApple={() => handleSocialSignIn("apple", signInWithApple)}
                />
              </div>
            </fieldset>

            {footnote ? (
              <p
                className="text-center text-[13px] font-medium text-ink/75 leading-snug"
                aria-hidden={loading}
              >
                {footnote.link ? (
                  <>
                    {footnote.text}{" "}
                    <Link
                      to={footnote.link.to}
                      tabIndex={loading ? -1 : undefined}
                      className="text-plum font-semibold underline underline-offset-2 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2"
                    >
                      {footnote.link.label}
                    </Link>
                    .
                  </>
                ) : (
                  footnote.text
                )}
              </p>
            ) : null}
          </div>
        </ReadableCard>
      </main>
    </div>
  );
}
